import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import api from '../../../utils/api';
import {
  normalizeStreamEvent,
  extractConversationIdFromStreamEvent,
  extractPartialAnswerFromStreamEvent,
  extractCompleteAnswerFromStreamEvent,
  conversationIdFromHttpResult,
  fullAnswerFromResponseArray,
} from '../../../utils/aiChatPayload';
import { AI_CHAT_CONVERSATION_STORAGE_KEY, AI_CHAT_MESSAGES_STORAGE_KEY } from './constants';
import {
  ADD_USER_MESSAGE,
  START_ASSISTANT_MESSAGE,
  UPDATE_STREAMING_ASSISTANT,
  RECONCILE_ASSISTANT,
  SET_CONVERSATION_ID,
  SET_SENDING,
  SET_ERROR,
  RESET,
  FREEZE_ASSISTANT_STREAM,
  RESTORE_MESSAGES,
  SET_AI_ACCESS,
} from './types';

// IDs of assistant messages that have received at least one live socket chunk.
// When the HTTP response arrives we skip the fallback replay for these.
const socketStreamedIds = new Set();

export default {
  restoreFromStorage({ commit, state }) {
    if (typeof sessionStorage === 'undefined') return;
    let storedConvId, storedMessages;
    try {
      storedConvId = sessionStorage.getItem(AI_CHAT_CONVERSATION_STORAGE_KEY);
      storedMessages = sessionStorage.getItem(AI_CHAT_MESSAGES_STORAGE_KEY);
    } catch {
      return;
    }
    if (!state.conversationId && storedConvId) {
      const trimmed = storedConvId.trim();
      if (trimmed) commit(SET_CONVERSATION_ID, trimmed);
    }
    if (storedMessages) {
      try {
        const parsed = JSON.parse(storedMessages);
        commit(RESTORE_MESSAGES, parsed);
      } catch {
        /* malformed storage — ignore */
      }
    }
  },

  handleSocketPayload({ commit, state }, rawPayload) {
    try {
      const eventObj = normalizeStreamEvent(rawPayload);
      if (!eventObj) return;

      const convId = extractConversationIdFromStreamEvent(eventObj);
      if (convId) {
        commit(SET_CONVERSATION_ID, convId);
      }

      const assistantId = state.activeStreamAssistantId;
      if (!assistantId) return;
      if (state.frozenAssistantStreamId === assistantId) return;

      // Completion event — finalize the message immediately.
      const complete = extractCompleteAnswerFromStreamEvent(eventObj);
      if (complete != null && complete !== '') {
        socketStreamedIds.add(assistantId);
        commit(RECONCILE_ASSISTANT, { id: assistantId, text: complete });
        commit(FREEZE_ASSISTANT_STREAM, assistantId);
        return;
      }

      // Partial chunk — render it directly, no typewriter delay.
      const partial = extractPartialAnswerFromStreamEvent(eventObj);
      if (partial != null) {
        socketStreamedIds.add(assistantId);
        commit(UPDATE_STREAMING_ASSISTANT, { id: assistantId, text: partial });
      }
    } catch (e) {
      console.warn('aiChat handleSocketPayload', e);
    }
  },

  async sendMessage({ commit, state }, { text, eventId }) {
    const trimmed = (text || '').trim();
    if (!trimmed || state.isSending) return;

    commit(SET_ERROR, null);
    const userMessageId = uuidv4();
    const assistantId = uuidv4();
    commit(ADD_USER_MESSAGE, { id: userMessageId, text: trimmed });
    commit(START_ASSISTANT_MESSAGE, { id: assistantId });
    const body = {
      message: trimmed,
      conversation_id: state.conversationId || null,
    };

    if (eventId != null && String(eventId).trim() !== '') {
      const idStr = String(eventId).trim();
      body.event_id = /^\d+$/.test(idStr) ? Number(idStr) : idStr;
    }

    // Release the composer immediately — socket chunks will drive the UI.
    commit(SET_SENDING, false);

    // Fire the POST in the background; it is only used for conv-id and error fallback.
    axios(api.aiChat.send(body)).then(({ data }) => {
      if (data.error) {
        // Only show error if socket never delivered anything.
        if (!socketStreamedIds.has(assistantId)) {
          commit(SET_ERROR, data.message || 'Chat request failed.');
          commit(RECONCILE_ASSISTANT, {
            id: assistantId,
            text: 'Sorry, something went wrong. Please try again.',
          });
          commit(FREEZE_ASSISTANT_STREAM, assistantId);
        }
        socketStreamedIds.delete(assistantId);
        return;
      }

      const result = data.result;
      if (!result) return;

      const httpConvId = conversationIdFromHttpResult(result);
      if (httpConvId) commit(SET_CONVERSATION_ID, httpConvId);

      // Socket already streamed this — nothing left to do.
      if (socketStreamedIds.has(assistantId)) {
        socketStreamedIds.delete(assistantId);
        return;
      }

      // Fallback: socket delivered nothing, render the HTTP batch response.
      const fullText = fullAnswerFromResponseArray(result.response);
      if (fullText) {
        commit(RECONCILE_ASSISTANT, { id: assistantId, text: fullText });
        commit(FREEZE_ASSISTANT_STREAM, assistantId);
      }
    }).catch((e) => {
      if (socketStreamedIds.has(assistantId)) {
        socketStreamedIds.delete(assistantId);
        return;
      }
      const msg = (e.response && e.response.data && e.response.data.message)
        || e.message
        || 'Network error.';
      commit(SET_ERROR, msg);
      commit(RECONCILE_ASSISTANT, {
        id: assistantId,
        text: 'Sorry, something went wrong. Please try again.',
      });
      commit(FREEZE_ASSISTANT_STREAM, assistantId);
    });
  },

  async checkAccess({ commit }) {
    try {
      const { data } = await axios(api.aiChat.access());
      commit(SET_AI_ACCESS, data.ai === true);
    } catch {
      commit(SET_AI_ACCESS, false);
    }
  },

  startNewConversation({ commit }) {
    commit(RESET);
  },

  reset({ commit }) {
    commit(RESET);
  },
};
